import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import DownloadIcon from '@mui/icons-material/Download';
import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DataGridPremium as DataGrid, GridColDef } from '@mui/x-data-grid-premium';
import { Document_FieldsFragment, useDocument_DownloadFilesMutation } from 'queries';

import DocumentPreviewModal from 'pages/common/document/document-preview-modal';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

const useStyles = makeStyles(() => ({
  container: {
    marginLeft: '20px',
  },
  emptyContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    marginTop: '8px',
  },
  title: {
    fontSize: '32px',
    fontWeight: '700',
    marginTop: '24px',
  },
  subtitle: {
    fontSize: '14px',
    marginTop: '12px',
  },
  dataGrid: {
    border: 0,
    borderRadius: '8px',
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
    '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader': {
      display: 'none',
    },
    '& .MuiDataGrid-virtualScroller': {
      marginTop: '0 !important',
    },
    '& .MuiDataGrid-row': {
      borderBottom: '1px solid #ECEEF1',
      borderTop: 'none',
    },
    '& .MuiDataGrid-cell': {
      borderBottom: 'none',
      borderTop: 'none',
      paddingLeft: '0px',
      paddingRight: '0px',
    },
  },
  documentTitle: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  documentSubtitle: {
    fontSize: '14px',
    marginRight: '8px',
  },
  downloadButton: {
    cursor: 'pointer',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '700',
  },
}));

type Props = {
  documents: Document_FieldsFragment[];
  onRefreshDocuments: () => void;
};

const AssignedSearchResultDocuments = ({ documents, onRefreshDocuments }: Props) => {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();
  const [selectedPreviewDocument, setSelectedPreviewDocument] = useState<Document_FieldsFragment>();

  const [downloadFile] = useDocument_DownloadFilesMutation({
    onCompleted: ({ data: downloadFilesData }) => {
      window.open(downloadFilesData[0].signedUrl, '_blank');
    },
    onError: (e) => {
      showAlert({
        message: 'Error downloading files: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const getTableColumns = useCallback(() => {
    const columns: GridColDef[] = [
      {
        flex: 2,
        field: 'name',
        renderCell: (params) => {
          return (
            <Box
              className={classes.documentTitle}
              onClick={() => setSelectedPreviewDocument(params.row)}
            >
              <Typography className={classes.documentSubtitle}>{params.value}</Typography>
            </Box>
          );
        },
      },
      {
        flex: 0.5,
        field: 'download',
        renderCell: (params) => (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              justifyContent: 'center',
            }}
          >
            <DownloadIcon
              className={classes.downloadButton}
              fontSize="small"
              onClick={() => {
                downloadFile({
                  variables: {
                    portalId,
                    ids: [params.row.id],
                  },
                });
              }}
            />
          </div>
        ),
      },
    ];

    return columns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRefreshDocuments, classes]);

  return (
    <Box className={classes.container}>
      <Typography className={classes.sectionTitle}>Search Correspondence</Typography>
      <DataGrid
        className={classes.dataGrid}
        columns={getTableColumns()}
        rows={documents}
        disableColumnReorder
        disableRowSelectionOnClick
        disableColumnMenu
        hideFooter
        columnHeaderHeight={0}
        autoHeight={true}
      />
      {selectedPreviewDocument && (
        <DocumentPreviewModal
          setShowModal={() => setSelectedPreviewDocument(undefined)}
          showModal={true}
          document={selectedPreviewDocument}
        />
      )}
    </Box>
  );
};

export default AssignedSearchResultDocuments;
